import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { useLoadableAction } from '../../../hooks/useLoadableAction';
import { TranscriptContextValue } from '../../../store/transcript/store';
import { OcChatConfig, PreviewState } from '../../../types/oc';
import { DataLoadable, isLoadSuccess, loadSuccess, notStarted } from '../../../utils/loadable';
import { MESSAGE_ROLE } from '../../Message/types/messages';
import { getChatHistory } from '../services/ocEndpoints';
import { ChatHistoryItem } from '../types/apiTypes';
import { deleteItem, loadSessionKey, saveLastActivityTime, usePersistSession } from './usePersistSession';
// Session persistence keys for global AI Fab / AI Assistant chat panel
const AI_FAB_SESSION_KEY = 'global-ai-fab-search-chat';
const AI_FAB_LAST_ACTIVITY_TIME_KEY = 'global-ai-fab-search-chat-last-activity-time';

export type SessionManager = {
    /**
     * The current session key being used
     */
    initialSessionKey: string | null | undefined;

    /**
     * The current active session ID (reactive)
     */
    currentSessionId: string | null | undefined;

    /**
     * The chat context ref used by TranscriptContextProvider
     */
    chatContextRef: MutableRefObject<TranscriptContextValue | null>;

    /**
     * Whether chat history should be shown
     */
    showChatHistory: boolean;

    /**
     * The chat history loadable (includes loading/error state and data)
     */
    historyLoadable: DataLoadable<ChatHistoryItem[]>;

    /**
     * Clears the current chat session and starts a new one
     */
    resetSession: () => void;

    /**
     * Load a specific session by key
     * @param sessionKey - The session key to restore
     */
    loadSession: (sessionKey: string) => void;

    /**
     * Callback to rename "New chat" sessions with first user message
     * @param sessionKey - The session key to update
     * @param firstMessage - The user's message text
     */
    renameSessionIfNeeded: (sessionKey: string, firstMessage: string) => void;
};

/**
 * Preview session manager hook for preview mode
 * Returns a session manager that uses mocked data instead of real API calls
 */
export const usePreviewSessionManager = (
    showChatHistory: boolean,
    previewState: PreviewState,
): SessionManager => {
    const sessionId = previewState.sessionId || 'preview-session-id';

    // Use mocked session ID as initial session
    const initialSessionKey = sessionId;
    const [currentSessionId, setCurrentSessionId] = useState<string | null | undefined>(initialSessionKey);

    // Create mocked history loadable from previewState
    const historyLoadable: DataLoadable<ChatHistoryItem[]> = useMemo(() => {
        if (!showChatHistory || !previewState.sessionHistory) {
            return notStarted();
        }
        return loadSuccess(previewState.sessionHistory);
    }, [showChatHistory, previewState.sessionHistory]);

    // Create a mock chatContextRef that doesn't persist to localStorage
    // Wrap in useMemo to prevent dependency issues
    const chatContextRef: MutableRefObject<TranscriptContextValue | null> = useMemo(
        () => ({ current: null }),
        []
    );

    const resetSession = useCallback(() => {
        chatContextRef.current?.resetSession?.();
        setCurrentSessionId(sessionId);
    }, [chatContextRef, sessionId]);

    const loadSession = useCallback(
        (sessionKey: string) => {
            if (chatContextRef.current?.loadSession) {
                setCurrentSessionId(sessionKey);
                chatContextRef.current.loadSession(sessionKey).catch(() => {
                    // Silently fail in preview mode
                });
            }
        },
        [chatContextRef]
    );

    const renameSessionIfNeeded = useCallback(() => {
        // No-op in preview mode
    }, []);

    return {
        initialSessionKey,
        currentSessionId,
        chatContextRef,
        showChatHistory,
        historyLoadable,
        resetSession,
        loadSession,
        renameSessionIfNeeded,
    };
};

/**
 * Custom hook to manage AI chat sessions with persistence
 * @param showChatHistory - Whether chat history should be shown and loaded
 * @param config - The configuration for the session manager
 * @param originToolId - The origin tool ID for filtering chat history
 * @param isAuthenticated - Whether the user is authenticated (chat history only loads when authenticated)
 * @returns SessionManager object with session management methods and state
 *  - ai-assistant TranscriptContext handles the chatContextRef for the chat use / activities
 *  - usePersistSession hook persists session changes and activity time to localStorage to persist across page changes
 *  - SessionManger handles session resets and selection changes
 */
export const useSessionManager = (showChatHistory: boolean, config: OcChatConfig, originToolId: string, isAuthenticated: boolean): SessionManager => {

    // Load initial session from localStorage
    const initialSessionKey = loadSessionKey(AI_FAB_SESSION_KEY, AI_FAB_LAST_ACTIVITY_TIME_KEY);

    // Track current session ID in state so it's reactive (initialize with existing session)
    const [currentSessionId, setCurrentSessionId] = useState<string | null | undefined>(initialSessionKey);

    // Only load history data if chat history should be shown
    const [serverHistoryLoadable, loadHistoryData] = useLoadableAction(getChatHistory);
    const [historyItems, setHistoryItems] = useState<ChatHistoryItem[]>([]);

    // Callback for when new sessions are created (called from usePersistSession)
    const handleNewSession = useCallback(
        (sessionId: string) => {
            // Update current session ID state
            setCurrentSessionId(sessionId);

            if (!showChatHistory) {
                return;
            }

            // Check if session already exists in local or server history
            setHistoryItems((prev) => {
                // A sessionId may be '' temporarily while loading a new session
                // If we have a real sessionId and there's a temporary empty one, update it
                if (sessionId !== '') {
                    const tempIndex = prev.findIndex((item) => item.ChatSessionId === '');
                    if (tempIndex !== -1) {
                        // Update the temporary entry with the real session ID
                        const updated = [...prev];
                        updated[tempIndex] = {
                            ...updated[tempIndex],
                            ChatSessionId: sessionId
                        };
                        return updated;
                    }
                }

                // Check local items
                const existsInLocal = prev.some((item) => item.ChatSessionId === sessionId);
                if (existsInLocal) {
                    return prev;
                }

                // Check server data (if loaded)
                if (isLoadSuccess(serverHistoryLoadable)) {
                    const existsInServer = serverHistoryLoadable.data?.some((item) => item.ChatSessionId === sessionId);
                    if (existsInServer) {
                        return prev; // Already in server history, no need to add locally
                    }
                }

                // Add new chat item to local state
                const newHistoryItem: ChatHistoryItem = {
                    ChatSessionId: sessionId,
                    // `Label: null` indicates a new unlabeled session; the label will be set when the first message is sent.
                    Label: null,
                    CreatedOn: new Date().toISOString(),
                    OriginTool: originToolId
                };

                return [newHistoryItem, ...prev];
            });
        },
        [showChatHistory, serverHistoryLoadable, originToolId]
    );

    const chatContextRef = usePersistSession(AI_FAB_SESSION_KEY, AI_FAB_LAST_ACTIVITY_TIME_KEY, handleNewSession);

    useEffect(() => {
        // Only load chat history if history should be shown AND user is authenticated
        if (showChatHistory && isAuthenticated) {
            loadHistoryData(config, originToolId);
        }
    }, [showChatHistory, isAuthenticated, config, originToolId, loadHistoryData]);

    // Merge server history into local state once it loads
    useEffect(() => {
        if (!showChatHistory || !isLoadSuccess(serverHistoryLoadable) || !serverHistoryLoadable.data) {
            return;
        }

        setHistoryItems((prev) => {
            // Get session IDs that are already in local state
            const localSessionIds = new Set(prev.map((item) => item.ChatSessionId));

            // Add server items that aren't already in local state
            const serverItemsToAdd = serverHistoryLoadable.data.filter(
                (item) => !localSessionIds.has(item.ChatSessionId)
            );

            const mergedItems = [...prev, ...serverItemsToAdd];

            // Check if initial session from localStorage exists in the merged list
            // If not, add it (this handles sessions with no messages that aren't returned by backend)
            if (initialSessionKey) {
                const initialSessionExists = mergedItems.some((item) => item.ChatSessionId === initialSessionKey);

                if (!initialSessionExists) {
                    const currentMessages = chatContextRef.current?.state.messages || [];
                    const firstUserMessage = currentMessages.find((msg) => msg.role === MESSAGE_ROLE.USER && !!msg.message.trim());

                    const newHistoryItem: ChatHistoryItem = {
                        ChatSessionId: initialSessionKey,
                        // Use first user message as label, or null for "New chat"
                        Label: firstUserMessage ? firstUserMessage.message.trim() : null,
                        CreatedOn: new Date().toISOString(),
                        OriginTool: originToolId
                    };

                    return [newHistoryItem, ...mergedItems];
                }
            }

            return mergedItems;
        });
    }, [serverHistoryLoadable, showChatHistory, initialSessionKey, chatContextRef, originToolId]);

    // Combine server and local history items into a single loadable
    const historyLoadable: DataLoadable<ChatHistoryItem[]> = useMemo(() => {
        if (!isLoadSuccess(serverHistoryLoadable)) {
            return serverHistoryLoadable;
        }

        // All items are now in historyItems (merged)
        return {
            ...serverHistoryLoadable,
            data: historyItems
        };
    }, [serverHistoryLoadable, historyItems]);

    // Callback to rename a session when the first user message is sent
    const renameSessionIfNeeded = useCallback(
        (sessionKey: string, message: string) => {
            if (!showChatHistory) {
                return;
            }

            // Use the message as the label (don't update if empty)
            const newLabel = message.trim();
            if (!newLabel) {
                return;
            }

            // Check if this is truly the first user message in the session
            // If there are already multiple messages, the session likely already has a label
            const currentMessages = chatContextRef.current?.state.messages || [];
            const validUserMessageCount = currentMessages.filter((msg) => msg.role === MESSAGE_ROLE.USER && !!msg.message.trim()).length;
            if (validUserMessageCount > 0) {
                return; // Not the first message with content, already has a label
            }

            // Use functional update to access latest historyItems
            setHistoryItems((prev) => {
                const historyItem = prev.find((item) => item.ChatSessionId === sessionKey);
                const needsRename = historyItem?.Label === null;

                if (!needsRename) {
                    return prev;
                }

                // Update the history item
                return prev.map((item) => (item.ChatSessionId === sessionKey ? { ...item, Label: newLabel } : item));
            });
        },
        [showChatHistory, chatContextRef]
    );

    const resetSession = useCallback(() => {
        chatContextRef.current?.resetSession?.();

        // Clear the persisted session data from localStorage
        // usePersistSession will update it when the new session is created
        deleteItem(AI_FAB_SESSION_KEY);
        deleteItem(AI_FAB_LAST_ACTIVITY_TIME_KEY);

        // The new session will be added automatically via the handleNewSession callback
    }, [chatContextRef]);

    const loadSession = useCallback(
        (sessionKey: string) => {
            if (chatContextRef.current?.loadSession) {
                // Update current session ID immediately for UI responsiveness
                setCurrentSessionId(sessionKey);

                chatContextRef.current
                    .loadSession(sessionKey)
                    .then(() => {
                        // usePersistSession will update the sessionKey in localStorage
                        // but we want to ensure the timer is reset after a new session is loaded
                        saveLastActivityTime(AI_FAB_LAST_ACTIVITY_TIME_KEY);
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error('Failed to load session:', error);
                    });
            }
        },
        [chatContextRef]
    );

    return {
        initialSessionKey,
        currentSessionId,
        chatContextRef,
        showChatHistory,
        historyLoadable,
        resetSession,
        loadSession,
        renameSessionIfNeeded
    };
};
